import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { adminOrgs } from "@/api/adminPanel";
import { ImpersonateStore } from "@/api/http";
import { useAuth } from "@/auth/AuthContext";
import { Badge } from "@/components/ui";

/**
 * Super-admin only org switcher. Sets/clears `X-Impersonate-Org` via
 * `ImpersonateStore` (sessionStorage so each tab can act as a different
 * org). Every authenticated request reads the override from there.
 *
 * Backend-side: non-superusers get the header silently ignored, so a
 * regular user who manually sets sessionStorage gains nothing — RLS
 * still scopes by their JWT-issued Principal.
 */
export function OrgSwitcher() {
  const { isSuperAdmin } = useAuth();
  const qc = useQueryClient();
  const [current, setCurrent] = useState<string | null>(ImpersonateStore.get());

  useEffect(() => {
    const onChange = () => setCurrent(ImpersonateStore.get());
    window.addEventListener("piggy:impersonate-change", onChange);
    return () => window.removeEventListener("piggy:impersonate-change", onChange);
  }, []);

  const orgs = useQuery({
    queryKey: ["admin-orgs"],
    queryFn: () => adminOrgs.list(),
    enabled: isSuperAdmin,
    staleTime: 60_000,
  });

  const currentOrg = useMemo(() => orgs.data?.find((o) => o.id === current), [orgs.data, current]);

  if (!isSuperAdmin) return null;

  return (
    <div className="flex items-center gap-2 text-xs">
      {current && (
        <Badge tone="warn">
          impersonating {currentOrg?.slug ?? current.slice(0, 8)}
        </Badge>
      )}
      <select
        value={current ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "") ImpersonateStore.clear();
          else ImpersonateStore.set(v);
          // All cached queries are stale under the new org context.
          qc.invalidateQueries();
        }}
        className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs"
      >
        <option value="">— act as own org —</option>
        {orgs.data?.map((o) => (
          <option key={o.id} value={o.id}>
            {o.slug} ({o.name})
          </option>
        ))}
      </select>
    </div>
  );
}
